// noinspection JSIgnoredPromiseFromCall

import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";

import { ValidationObserver, ValidationProvider } from "vee-validate";

import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { optimizePhotos, uploadImage } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import { EventLocationsMixin } from "@/mixins/EventLocations.ts";

import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";

import { CreateEvent } from "@/models/calendar/CreateEvent";

import { RecurringEventsType } from "@/enums/RecurringEventsType";
import { Calendar } from "@/models/calendar/Calendar";
import { FacilitySettingsModel } from "@/models/FacilitySettings";
import { ValidationRules } from "@/utils/validation-rules";
import { PremiumServiceEventTypes } from "@/enums/PremiumServiceEventTypes";
import { PremiumServiceParticipantsLimit } from "@/enums/PremiumServiceParticipantsLimit";
import { convertTimeDurationTo24hFormat } from "@/utils/dateTimeFormat";
import { toTranslationKey } from "@/utils/stringFormat";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import ScheduledPublishEventsDialog from "@/views/calender/view-calendar-item/ScheduledPublishEventsDialog.vue";
import {GalleryImage} from "@/models/GalleryImage";
import * as firebase from "firebase";
import {waitResizedImage} from "@/utils/wait-resized-image";

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
    GalleryPhotosUploadDlg,
    ConfirmDlg,
    TimePicker,
    ScheduledPublishEventsDialog
  },
  mixins: [EventLocationsMixin]
})
export default class ManipulateCalendarEventDialog extends Vue {
  @Prop() isEdit;
  @Prop() inviteesId;
  @Prop() eventType;
  @Prop() calendarData: Calendar = new Calendar();

  eventData: CreateEvent = new CreateEvent();
  noEndDate = false;

  dialog = false;
  loading = false;
  isSaving = false;
  startDateMenu = false;
  endDateMenu = false;
  isOpenScheduldEventsDialog = false;
  participantLimitNumber = 1;

  pickers = {
    start: false,
    end: false
  };

  time = {
    start: moment().format("HH:mm"),
    end: moment()
      .add(30, "m")
      .format("HH:mm")
  };

  timeFormat = '';

  recurringType = RecurringEventsType;
  recurringTypes = Object.values(RecurringEventsType).map(x=>({text:toTranslationKey(x), value:x}))

  get currentFacility(): string {
    return this.$store.state.authModule.currentFacility;
  }

  get recurringEventsUpdated(): string {
    return this.$store.state.calenderModule.recurringEventsUpdated;
  }

  get startDate() {
    return this.eventData && this.eventData.startDate
      ? moment(this.eventData.startDate).format('ddd, '+ this.facility.dateFormat)
      : moment(new Date()).format('ddd, '+ this.facility.dateFormat);
  }

  get endDate() {
    return this.eventData && this.eventData.endDate
      ? moment(this.eventData.endDate).format('ddd, '+ this.facility.dateFormat)
      : moment(new Date()).format('ddd, '+ this.facility.dateFormat);
  }

  get rules(): object {
    return {
      start: [ValidationRules.required],
      end: [
        ValidationRules.required,
        () => {
          const start = moment(this.time.start, "h:mma");
          const end = moment(this.time.end, "h:mma");
          return (
            moment(end).isAfter(start) ||
            "Event end date cannot be before start date"
          );
        }
      ],
      email: [ValidationRules.email]
    };
  }

  get facility(): FacilitySettingsModel {
    return this.$store.state.facilityModule.selectedFacility;
  }

  get additionalService() {
    const additionalService = this.$store.state.additionalServicesModule.additionalService;
    if(!additionalService) return null

    additionalService.type = additionalService.type || PremiumServiceEventTypes.EventBased;

    return additionalService;
  }

  get isParticipantLimitTypesLimitPerEvent() {
    return this.additionalService?this.additionalService.participantLimit === PremiumServiceParticipantsLimit.LimitPerEvent: false;
  }

  get isWaitingList() {
    return this.additionalService ? this.additionalService.participantLimit !== PremiumServiceParticipantsLimit.SetLimit : false;
  }

  get formattedStartTime(){
    if(!this.facility.timeFormat) return ''

    return moment(this.time.start, 'HH:mm').format(this.facility.timeFormat)
  }

  get formattedEndTime(){
    if(!this.facility.timeFormat) return ''

    return moment(this.time.end, 'HH:mm').format(this.facility.timeFormat)
  }

  mounted(){
    this.timeFormat = this.facility.timeFormat.includes('A')?'ampm':'24h';

    if(this.eventData?.wingsIds.length) {
      (this as any).selectedWingsIds = this.eventData.wingsIds;
    }
  }

  @Watch("time.start")
  onStartDateChange(start: string) {
    this.time.end = moment(start, "h:mma")
      .add(1, "h")
      .format("HH:mm");
  }

  @Watch("recurringEventsUpdated")
  onRecurringEventsUpdated() {
    this.loadEvents()
  }

  @Watch('dialog') onOpenDialog(value) {
    if(value && this.additionalService) {
      this.eventData.isWaitingList = this.additionalService.isWaitingList;
    }
  }

  @Watch("selectedWingsIds")
  onSelectedWingsIds(newValue) {
    this.eventData.wingsIds = newValue;
  }

  @Emit("load-events") loadEvents() {
    return true;
  }

  async saveEventImage(e) {
    const path = `${this.currentFacility}/events/coverImages/${uuidv4()}`;
    const file: File | Blob | any = await optimizePhotos((e.target as HTMLInputElement).files[0], '');
    const fileSizeInMegabytes = fileSizeToMB(file?.size);

    if (!validFileSize10MB(fileSizeInMegabytes)) {
      return;
    }

    uploadImage({
      path,
      file,
      onStart: () => {
        this.loading = true;
      },
      onComplete: async storage => {
        const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
        const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

        try {
          const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
          this.eventData.imageUrl = uploadedImageUrl;
          this.eventData.thumbnailUrl = resizedImageUrl;
        } catch (e) {
          this.eventData.imageUrl = uploadedImageUrl;
          this.eventData.thumbnailUrl = uploadedImageUrl;
        }

        this.loading = false;
      }
    });
  }

  openScheduldEventsDialog() {
    this.isOpenScheduldEventsDialog = true
  }

  closeScheduldEventsDialog() {
    this.isOpenScheduldEventsDialog = false
  }

  onScheduldEventsDialogResponse(response) {
    this.saveAsDraft(response.isScheduled === true ? response.scheduledPublishDateTime : null)
    this.closeScheduldEventsDialog()
  }

  saveAsDraft(scheduledPublishDateTime){
    this.saveEventChanges(false, scheduledPublishDateTime)
  }

  publishEvent(){
    this.saveEventChanges(true, null)
  }

  saveEventChanges(isPublished: boolean, scheduledPublishDateTime: any) {
    
    this.isSaving = true
    const formData = { ...this.eventData };
    let eventsData: Calendar[] = [];

    formData.channelId = this.eventData.isVirtualEvent ? uuidv4() : null;
    formData.isPublished = isPublished;

    if (!formData.startDate) {
      formData.startDate = moment().format("YYYY-MM-DD");
    }

    if (isPublished) {
      formData.scheduledPublishDateTime = null
    }

    formData.scheduledPublishDateTime = scheduledPublishDateTime || null

    if (!formData.endDate) {
      formData.endDate = !formData.endDate
        ? formData.startDate
        : formData.endDate;
    }
   
    formData.duration = convertTimeDurationTo24hFormat(`${this.time.start}-${this.time.end}`);

    formData.type = this.eventType;

    formData.invitees = [this.$route.params.id];

    if (!formData.isRecurring) {
      this.$store
        .dispatch("setupCalendarEvent", {
          newEventData: formData
        })
        .then(event => {
          eventsData.push(event);
          this.isSaving = false
          this.saveChangesAndCloseDialog(eventsData);
        });
    } else {
      this.$store
        .dispatch("getRecurringEvents", {
          eventFormData: formData,
          noEndDate: this.noEndDate,
          invitees: [this.$route.params.id],
          monthlyRepeatOn: formData.monthlyRepeatOn
        })
        .then(events => {
          eventsData = events;
          this.isSaving = false
          this.saveChangesAndCloseDialog(eventsData);
        });
    }
  }

  private saveChangesAndCloseDialog(eventsData) {
    this.isEdit ? this.updateEvent(eventsData) : this.createEvent(eventsData);

    this.inviteesId = "";
    this.eventData = new CreateEvent();
    this.dialog = false;
  }

  close() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.$refs.confirmationModal as any)
      .open(
        null,
      this.isEdit ? this.$i18n.messages[this.$i18n.locale]['ManipulateCalendarEventDialog.CancelEditsMessage']: this.$i18n.messages[this.$i18n.locale]['ManipulateCalendarEventDialog.CancelCreationMessage'],
      false
      )
      .then(response => {
        if (response && response.agree) {
          this.inviteesId = "";
          this.eventData = new CreateEvent();
          this.dialog = false;
        }
      });
  }

  createEvent(eventsData: Calendar[]) {
    if (eventsData && eventsData.length === 1) {
      this.$store.dispatch("createNewCalenderEvent", eventsData[0]);
      this.loadEvents();
    } else {
      this.$store.dispatch("createNewCalenderEvents", { events: eventsData });
    }


    this.dialog = true;
  }

  updateEvent(eventsData: Calendar[]) {
    if (eventsData && eventsData.length === 1) {
      this.$store.dispatch("updateCalenderEvent", eventsData[0]);
    } else {
      this.$store.dispatch("updateCalendarEvents", eventsData);
    }

    this.loadEvents();
    this.dialog = true;
  }

  deleteEvent() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.$refs.confirmationModal as any)
      .open(null, "Are you sure you want to delete the event?", false)
      .then(response => {
        if (response && response.agree) {
          this.$store.dispatch("deleteCalenderEvent", {id: this.eventData.id, isRecurring: this.eventData?.isRecurring, office365EventId: this.eventData?.office365EventId});
          this.loadEvents();
          this.dialog = false;
        }
      });
  }

  public increment(): void {
    this.eventData.participantLimitNumber++;
    this.participantLimitNumber = this.eventData.participantLimitNumber;
  }

  public decrement(): void {
    if(this.eventData.participantLimitNumber > 1) {
      this.eventData.participantLimitNumber--;
      this.participantLimitNumber = this.eventData.participantLimitNumber;
    }
  }

  openGalleryPhotos() {
    (this.$refs.GalleryPhotosUploadDlg as any).open();
  }

  uploadGalleryPhoto(image: GalleryImage) {
    this.eventData.imageUrl = image.image;
    this.eventData.thumbnailUrl = image?.thumbnailUrl ?? image.image;
  }

  created() {
    if (this.isParticipantLimitTypesLimitPerEvent && this.additionalService) {
      this.eventData.participantLimit = this.additionalService.participantLimit;
      this.eventData.participantLimitNumber = 1;
    }
  }
}
